import React, { useEffect, useState } from "react";
import { Button, ButtonGroup, Col, Dropdown, Row, Spinner } from "react-bootstrap";
import { fetchReportInteractions } from "../../services/httpClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/free-solid-svg-icons";
import html2canvas from "html2canvas";
import ReportTimeInterval from "./ui/ReportTimeInterval";
import useApp from "../../utils/useApp";
import { InteractionsTotalMetric } from "./charts-metrics/InteractionsTotalMetric";
import { InteractionsBarChart } from "./charts-metrics/InteractionsBarChart";
import { InteractionsTable } from "./charts-metrics/InteractionsTable";
import ExportModal from "../ui/ExportModal";

const InteractionTab = ({ buildPDF, canvas, setCanvas, iPad, isActive }: any) => {
  const { auth }: any = useApp();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startDate, setStartDate] = useState<string>();
  const [endDate, setEndDate] = useState<string>();
  const [dateInterval, setDateInterval] = useState<number>(365);
  const [total, setTotal] = useState<number>();
  const [targetTypes, setTargetTypes] = useState<string[]>();
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const [visibleNoteCounts, setVisibleNoteCounts] = useState<any[]>();
  const [allNoteCounts, setAllNoteCounts] = useState<any[]>([]);
  const [showAllCounts, setShowAllCounts] = useState<boolean>(false);
  const [archivedNotesExist, setArchivedNotesExist] = useState<boolean>(false);
  const [showArchived, setShowArchived] = useState<boolean>(false);
  const [barData, setBarData] = useState<any>([]);
  const [tableData, setTableData] = useState<any>([]);
  const [interactionExportData, setInteractionExportData] = useState<any>();
  const [reportRunning, setReportRunning] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [isExportLoading, setIsExportLoading] = useState<boolean>(false);

  useEffect(() => {
    const start = new Date();
    start.setDate(start.getDate() - dateInterval);
    setStartDate(start.toISOString().slice(0, 10));
    setEndDate(new Date().toISOString().slice(0, 10));
    getInteractions(start.toISOString().slice(0, 10), new Date().toISOString().slice(0, 10));
    setVisibleCount(3);
  }, [auth?.profile?.userOrgID]);

  useEffect(() => {
    if (canvas && reportRunning) {
      buildPDF();
      setReportRunning(false);
    }
  }, [canvas, reportRunning, buildPDF]);

  useEffect(() => {
    if (!allNoteCounts) return;

    const archivedNotesWithData = allNoteCounts.some((noteCount) => {
      if (noteCount?.type === "user") {
        return noteCount.is_published === 0 && noteCount.count > 0;
      } else {
        return noteCount.is_archived === 1 && noteCount.count > 0;
      }
    });
    setArchivedNotesExist(archivedNotesWithData);

    const activeNotes = allNoteCounts.filter((noteCount) => {
      const isPublished = noteCount.is_published === 1 && noteCount.is_archived !== 1;
      return isPublished;
    });

    let archivedNotes: any[] = [];
    if (showArchived) {
      archivedNotes = allNoteCounts.filter((noteCount) => {
        const isArchived = noteCount.is_archived === 1 || (noteCount.type === "user" && noteCount.is_published === 0);
        return isArchived && noteCount.count > 0;
      });
    }

    const combinedCounts = [...activeNotes, ...archivedNotes];
    const visibleNotes = showAllCounts ? combinedCounts : combinedCounts.slice(0, visibleCount);
    setVisibleNoteCounts(visibleNotes);
  }, [showAllCounts, allNoteCounts, visibleCount, showArchived]);

  const getInteractions = (start: string, end: string) => {
    // Request the aggregated summary for fast chart rendering
    fetchReportInteractions(start, end, true)
      .then(async (res: any) => {
        const data = res[0].data || {};

        const noteTypesArr = data.note_type_counts || [];
        const filteredNoteTypes = noteTypesArr?.filter((t: any) => t?.type !== "system");
        const noteTypeNames = filteredNoteTypes.map((n: any) => n.name);
        setTargetTypes(noteTypeNames);
        setTotal(data.total || 0);

        // const allCounts: any[] = noteTypesArr.map((n: any) => ({ type: n.name, count: parseInt(n.count) }));
        setAllNoteCounts(filteredNoteTypes);

        const creators = data.creator_counts || [];
        setBarData(
          creators.map((c: any) => ({
            created_by: c.created_by,
            creator_firstname: c.firstname,
            creator_lastname: c.lastname,
            count: parseInt(c.count),
          }))
        );

        const table = data.creator_note_type_totals || [];
        // ensure totals have 0 for missing types
        const normalizedTable = table.map((t: any) => {
          const totals = {} as Record<string, number>;
          for (const nt of noteTypeNames) {
            totals[nt] = t.totals && t.totals[nt] ? t.totals[nt] : 0;
          }
          return { ...t, totals };
        });
        setTableData(normalizedTable);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const downloadReport = async () => {
    try {
      await wait(1000);
  
      const reportSection = document.getElementById("interaction-section");
      const reportTitle = document.getElementById("interaction-title");
      const refreshButton = document.getElementById("interaction-refresh-button");
      const showArchivedButton = document.getElementById("show-archived-button");
      const showLessButton = document.getElementById("show-less-button");
      const barChart = document.getElementById("interaction-bar");

      if (!reportSection) {
        console.error("Report section not found");
        setReportRunning(false);
        return;
      }
  
      if (reportTitle) {
        reportTitle.style.visibility = "hidden";
        reportTitle.style.display = "none";
      }

      if (refreshButton) refreshButton.style.visibility = "hidden";
      if (showArchivedButton) showArchivedButton.style.visibility = "hidden";
      if (showLessButton) showLessButton.style.visibility = "hidden";

      if (barChart) {
        barChart.style.visibility = "hidden";
        barChart.style.display = "none";
      }

      const images = reportSection.getElementsByTagName("img");
      for (let i = 0; i < images.length; i++) {
        images[i].crossOrigin = "anonymous";
      }

      const canvas = await html2canvas(reportSection, { useCORS: true });
      setCanvas(canvas);

      if (reportTitle) {
        reportTitle.style.visibility = "visible";
        reportTitle.style.display = "flex";
      }

      if (refreshButton) refreshButton.style.visibility = "visible";
      if (showArchivedButton) showArchivedButton.style.visibility = "visible";
      if (showLessButton) showLessButton.style.visibility = "visible";

      if (barChart) {
        barChart.style.visibility = "visible";
        barChart.style.display = "flex";
      }
    } catch (err) {
      console.error("Failed to generate interaction report", err);
      setReportRunning(false);
    }
  };

  const onClickDownloadReport = () => {
    setCanvas(null);
    setReportRunning(true);
    setShowArchived(true);

    if (allNoteCounts?.length > visibleCount) {
      setShowAllCounts(true);
      setShowArchived(true);
    }

    downloadReport();
  };

  useEffect(() => {
    if (showExportModal && startDate && endDate) {
      setIsExportLoading(true);
      // fetch full rows only for export
      fetchReportInteractions(startDate, endDate, false)
        .then((res: any) => {
          const allNotes = res[0].data || [];
          setInteractionExportData(allNotes);
        })
        .catch((err: any) => console.error(err))
        .finally(() => {
          setIsExportLoading(false);
        });
    }
  }, [showExportModal, startDate, endDate]);

  const noteTypeNames = Array.from(new Set(interactionExportData?.map((n: any) => n.name)));
  
  if (isLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" style={{ color: "#F6893D" }} />
      </div>
    );
  }

  return (
    <div id="interaction-section">
      <Row id="interaction-title">
        <Col className="col-8">
          <h4 className="hero-heading app-header-text">Interactions</h4>
        </Col>
        <Col className="text-end col-4">
          {!iPad && (
            <>
              <Dropdown as={ButtonGroup}>
                <Dropdown.Toggle id="dropdown-custom-1" className="app-primary-color button">
                  <FontAwesomeIcon icon={faDownload} className="me-2" />
                  Downloads
                </Dropdown.Toggle>
                <Dropdown.Menu className="super-colors">
                  <Dropdown.Item eventKey="1" onClick={() => onClickDownloadReport()}>
                    Report
                  </Dropdown.Item>
                  <Dropdown.Item eventKey="2" onClick={() => setShowExportModal(true)}>
                    Data Export
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
              <ExportModal
                showModal={showExportModal}
                setShowModal={setShowExportModal}
                interactionExportData={interactionExportData}
                noteTypes={noteTypeNames}
                startDate={startDate}
                endDate={endDate}
                isLoading={isExportLoading}
              />
            </>
          )}
        </Col>
      </Row>
      <Row>
        <Col>
          <ReportTimeInterval
            startDate={startDate}
            setStartDate={setStartDate}
            endDate={endDate}
            setEndDate={setEndDate}
            dateInterval={dateInterval}
            setDateInterval={setDateInterval}
            intervalOne={30}
            intervalOneText={"30 Days"}
            intervalTwo={90}
            intervalTwoText={"90 Days"}
            intervalThree={180}
            intervalThreeText={"180 Days"}
            intervalFour={365}
            intervalFourText={"Last Year"}
            onClickRefresh={getInteractions}
            hideBtnId="interaction-refresh-button"
          />
        </Col>
      </Row>
      <Row className="mb-3">
        <Col xs={3} className="p-2">
          <InteractionsTotalMetric title={"All Interactions"} data={total} />
        </Col>
        {visibleNoteCounts?.map((n: any, index: number) => (
          <Col key={index} xs={3} className="p-2">
            <InteractionsTotalMetric key={n?.name} title={n?.name} data={n?.count} />
          </Col>
        ))}
        <Col xs={12} className="text-center d-flex justify-content-center gap-3">
          {allNoteCounts?.length > visibleCount && !showAllCounts && (
            <Button className="app-header-text" variant="link" onClick={() => setShowAllCounts(true)}>
              {"Show More"}
            </Button>
          )}
          {archivedNotesExist &&
            (showAllCounts || allNoteCounts.length <= visibleCount) && (
              <Button id="show-archived-button" className="app-header-text" variant="link" onClick={() => setShowArchived((prev) => !prev)}>
                {showArchived ? "Hide Archived" : "View Archived"}
              </Button>
          )}
          {allNoteCounts?.length > visibleCount && showAllCounts && (
            <Button
              id="show-less-button"
              className="app-header-text"
              variant="link"
              onClick={() => {
                setShowAllCounts(false);
                setShowArchived(false);
              }}
            >
              {"Show Less"}
            </Button>
          )}
        </Col>
      </Row>
      <Row id="interaction-bar" className="mb-3">
        <Col className="col-12">
          <InteractionsBarChart data={barData} isActive={isActive} />
        </Col>
      </Row>
      {targetTypes && targetTypes?.length > 0 && (
        <Row className="mb-5">
          <Col xs={12}>
            <InteractionsTable data={tableData} types={targetTypes} />
          </Col>
        </Row>
      )}
    </div>
  );
};

export default InteractionTab;
